import { catalogRequestError, duplicateInternalCodeMessage, hasInternalCodeConflict } from "../internalCatalogCodes";
import { useRef, useState } from "react";
import axios from "../../../api/axiosInstance";
import { managedByAdmin } from "../../utils/tenderManagement";
import { readItemsFamiliesUser } from "../itemsFamiliesUi";
import { useCatalogTaxonomyDialog, useCatalogTaxonomyText } from "../catalogTaxonomyMessages";
import CatalogCreationPendingNotice, { isPendingCatalogCreation } from "../CatalogCreationPendingNotice";
import useCatalogAccess from "../useCatalogAccess";

const LEVELS = {
  family: { label: "Family", resource: "families" },
  subfamily: { label: "Subfamily", resource: "subfamilies" },
  category: { label: "Category", resource: "categories" },
  subcategory: { label: "Subcategory", resource: "subcategories" },
};

export default function EditTaxonomyPopup({ kind, record, onClose, onSaved, existingRecords = [] }) {
  const t = useCatalogTaxonomyText();
  const level = LEVELS[kind];
  const user = readItemsFamiliesUser();
  const businessUnitId = useRef(user?.active_business_unit_id).current;
  const access = useCatalogAccess(businessUnitId);
  const group = ["family", "subfamily"].includes(kind) ? "items" : "categories";
  const permissions = [`purchasing.${group}.create`, `purchasing.${group}.update`];
  const canEdit = access.can(permissions);
  const editable = Boolean(record?.id) && canEdit && !managedByAdmin(record);
  const [form, setForm] = useState(() => ({ code: record?.code || "", name: record?.name || "", description: record?.description || "" }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [pendingRequest, setPendingRequest] = useState(null);
  const pending = useRef(false);
  const close = () => { if (!pending.current) onClose(); };
  const dialogRef = useCatalogTaxonomyDialog(editable && !pendingRequest, close, saving);

  const submit = async (event) => {
    event.preventDefault();
    if (!editable || !access.can(permissions) || pending.current) return;
    const stamp = access.capture();
    const payload = { code: form.code.trim(), name: form.name.trim(), description: form.description.trim() };
    if (!payload.code || !payload.name) { setError("Code and name are required."); return; }
    if (hasInternalCodeConflict(payload.code, existingRecords, record.id)) {
      setError(duplicateInternalCodeMessage(payload.code));
      return;
    }
    pending.current = true;
    setSaving(true);
    setError("");
    try {
      const { data } = await axios.put(`/api/purchasing/${level.resource}/${record.id}`, payload, access.requestConfig(permissions));
      if (!access.isCurrent(stamp, permissions)) return;
      if (isPendingCatalogCreation(data)) { setPendingRequest(data.request); return; }
      await onSaved?.({ ...record, ...payload, ...data, id: record.id });
      onClose();
    } catch (requestError) {
      if (!access.isCurrent(stamp, permissions)) return;
      setError(catalogRequestError(requestError, `Error saving ${kind}.`, form.code));
    } finally {
      pending.current = false;
      setSaving(false);
    }
  };

  if (!editable) return null;
  if (pendingRequest) return <CatalogCreationPendingNotice request={pendingRequest} onClose={onClose} />;
  const change = (event) => setForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  return <div className="spx-modalBackdrop" onClick={(event) => { if (event.target === event.currentTarget) close(); }}>
    <div ref={dialogRef} className="spx-modal" role="dialog" aria-modal="true" aria-label={t(`Edit ${level.label}`)}>
      <div className="spx-modalHeader">
        <div>
          <div className="spx-modalKicker">{t("Catalog Structure")}</div>
          <h3 className="spx-modalTitle">{t(`Edit ${level.label}`)}</h3>
          <p className="spx-modalSubtitle">{t("Update the code, name and description for this classification.")}</p>
        </div>
        <button type="button" className="spx-closeBtn" onClick={close} disabled={saving} aria-label={t("Close")}>×</button>
      </div>
      <form onSubmit={submit} className="spx-modalBody">
        <div className="spx-formGrid">
          <div className="spx-field">
            <label className="spx-label" htmlFor={`${kind}_edit_code`}>{t(`${level.label} Code`)}</label>
            <input id={`${kind}_edit_code`} name="code" className="spx-input" value={form.code} onChange={change} disabled={saving} maxLength={50} required />
          </div>
          <div className="spx-field">
            <label className="spx-label" htmlFor={`${kind}_edit_name`}>{t(`${level.label} Name`)}</label>
            <input id={`${kind}_edit_name`} name="name" className="spx-input" value={form.name} onChange={change} disabled={saving} maxLength={120} required />
          </div>
          <div className="spx-field spx-field--full">
            <label className="spx-label" htmlFor={`${kind}_edit_description`}>{t("Description")}</label>
            <textarea id={`${kind}_edit_description`} name="description" className="spx-textarea" value={form.description} onChange={change} disabled={saving} />
          </div>
        </div>
        {error && <div className="spx-alert" role="alert">{t(error)}</div>}
        <div className="spx-modalFooter">
          <button type="button" className="spx-btn spx-btn--ghost" disabled={saving} onClick={close}>{t("Cancel")}</button>
          <button type="submit" className="spx-btn spx-btn--primary" disabled={saving}>{t(saving ? "Saving..." : "Save changes")}</button>
        </div>
      </form>
    </div>
  </div>;
}
